import type { UserLike } from '@/Ship/Helpers/fieldViews'

/**
 * Права картки перевізника — `_permissions` у відповіді з `?permissions=true`
 * (`SPEC-010` §14). Фронт показує дії лише за ними, ролей сам не питає
 * (`SPEC-003` §1.3): правило «лише логіст» (R-06) знає `CarrierRule`, а
 * ролі з входу — ні.
 */
export interface CarrierPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    comment?: boolean
    send_to_verification?: boolean
    verify?: boolean
    reject_verification?: boolean
    blacklist?: boolean
    unblacklist?: boolean
}

/** Статус перевізника — вкладений рядок довідника `carrier_statuses` (`status_list:short:read`). */
export interface CarrierStatusRef {
    '@id'?: string
    uuid?: string
    code?: string
    title?: string
    color?: string
}

/** Значення картки так, як їх тримає форма (лише те, що читає фронт). */
export interface CarrierCardValues {
    uuid?: string
    '@id'?: string
    title?: string
    status?: CarrierStatusRef | null
    /** Верифікований — обчислюване зі статусу; від нього — «Друковані форми» (R-13) */
    isVerified?: boolean
    phones?: string[] | null
    emails?: string[] | null
    responsibles?: UserLike[] | null
    _permissions?: CarrierPermissions
    [key: string]: unknown
}

/** Те, що шаблон картки бере з ядрового `formContext`. */
export interface CarrierFormContextLike {
    isEditable?: boolean
    values?: CarrierCardValues
    getError?: (path: string) => unknown
    setInitialValue?: (path: string, value: unknown) => void
}

/** Те, що шаблон картки бере з ядрового `crudContext`. */
export interface CarrierCrudContextLike {
    resource?: { key?: string; restUrl?: string; fields?: Record<string, unknown> }
}

/** Колекція перевізників — від неї й адреси історії, лічильників і експорту (`SPEC-010` §7.1). */
export const CARRIER_REST_URL = '/services/carriers'

/** Контакти перевізника (`SPEC-010` §7.3): префільтр `carrier.uuid`, ресурс `Crm.CarrierContact`. */
export const CARRIER_CONTACT_REST_URL = '/services/carrier_contacts'

/** Ключ ресурсу контакту перевізника — під ним його відкриває вкладка «Контакти». */
export const CARRIER_CONTACT_RESOURCE = 'Crm.CarrierContact'

/** Коментарі перевізника (`SPEC-010` §7.3, ADR 027): поле власника — `carrier`. */
export const CARRIER_COMMENT_REST_URL = '/services/carrier_comments'

/** Коментарі контакту перевізника: поле власника — `contact`. */
export const CARRIER_CONTACT_COMMENT_REST_URL = '/services/carrier_contact_comments'

/** Договори перевізника (`SPEC-010` §7.3): префільтр `carrier.uuid`, ресурс `Crm.CarrierContract`. */
export const CARRIER_CONTRACT_REST_URL = '/services/carrier_contracts'

/** Крок 1 двокрокового завантаження файлу договору (`SPEC-008` §5.7.1). */
export const CARRIER_CONTRACT_FILE_UPLOAD_URL = '/services/carrier_contract_files'

/** Ключ ресурсу договору перевізника — під ним його відкриває вкладка «Договори». */
export const CARRIER_CONTRACT_RESOURCE = 'Crm.CarrierContract'

/** Документи перевізника (`SPEC-010` §7.3): префільтр `carrier.uuid`, ресурс `Crm.CarrierDocument`. */
export const CARRIER_DOCUMENT_REST_URL = '/services/carrier_documents'

/** Ключ ресурсу документа перевізника — під ним його відкриває вкладка «Друковані форми». */
export const CARRIER_DOCUMENT_RESOURCE = 'Crm.CarrierDocument'

/** Довідник статусів перевізника (`SPEC-007` §3.3) — з нього рядок статусів і поле фільтра. */
export const CARRIER_STATUS_RESOURCE = 'Setting.ListCarrierStatus'
